import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { Building2, History, Menu, SlidersHorizontal, Users } from 'lucide-react';
import { can, type Role } from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { useTranslation, type CopyKey } from '@/lib/i18n';

type Entry = { to: string; label: CopyKey; icon: typeof Users };

// The map fills the screen; everything else in the app hangs off this menu. §20
export function entriesFor(role: Role, operator: boolean): Entry[] {
  const membership = { role, status: 'active' } as const;
  const entries: Entry[] = [];
  if (can(membership, 'venue:team')) entries.push({ to: '/team', label: 'team', icon: Users });
  if (can(membership, 'venue:settings'))
    entries.push({ to: '/settings', label: 'settings', icon: SlidersHorizontal });
  if (can(membership, 'reservation:activity'))
    entries.push({ to: '/activity', label: 'activity', icon: History });
  if (operator) entries.push({ to: '/platform', label: 'platformManage', icon: Building2 });
  return entries;
}

export function HeaderMenu({
  role,
  operator,
}: {
  role: Role;
  operator: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const entries = entriesFor(role, operator);
  if (entries.length === 0) return null;

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        aria-label={t('menu')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        <Menu className="size-4" aria-hidden />
      </Button>
      {open && (
        <>
          <button
            type="button"
            aria-label={t('close')}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute end-0 z-50 mt-1 w-56 overflow-hidden rounded-xl border bg-card py-1 shadow-lg"
          >
            {entries.map((entry) => (
              <Link
                key={entry.to}
                role="menuitem"
                to={entry.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary"
              >
                <entry.icon className="size-4 shrink-0" aria-hidden />
                {t(entry.label)}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
